import { useState } from 'react';
import { Result as PointDataResult } from '../../../services/models/PointDataResponse';
import { FilterSelection, MapAd } from '../utils/offerFilters';

// Form & Search State
export const useFormState = () => {
  const [selectedRegion, setSelectedRegion] = useState<{
    name: string;
    key: string;
  }>({ name: "", key: "" });
  const [selectedCity, setSelectedCity] = useState<{
    name: string;
    key: string;
  }>({ name: "", key: "" });
  const [selectedDistrict, setSelectedDistrict] = useState<{
    name: string;
    key: string;
  }>({ name: "", key: "" });
  const [selectedPlan, setSelectedPlan] = useState<{
    name: string;
    key: string;
  }>({ name: "", key: "" });
  const [selectedParcel, setSelectedParcel] = useState<{
    name: string;
    key: string;
  }>({ name: "", key: "" });
  const [propertyNumber, setPropertyNumber] = useState<string>("");
  const [landAddress, setLandAddress] = useState<string>("");
  const [parcelNumberOrText, setParcelNumberOrText] = useState<string>("");

  return {
    selectedRegion, setSelectedRegion,
    selectedCity, setSelectedCity,
    selectedDistrict, setSelectedDistrict,
    selectedPlan, setSelectedPlan,
    selectedParcel, setSelectedParcel,
    propertyNumber, setPropertyNumber,
    landAddress, setLandAddress,
    parcelNumberOrText, setParcelNumberOrText,
  };
};

// Data Arrays State
export const useDataArraysState = () => {
  const [regions, setRegions] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [parcels, setParcels] = useState<any[]>([]);
  return {
    regions, setRegions,
    cities, setCities,
    districts, setDistricts,
    plans, setPlans,
    parcels, setParcels,
  };
};

// UI State (Modals & Loading)
export const useUIState = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterModalIsOpen, setFilterModalIsOpen] = useState<boolean>(false);
  const [legendModalIsOpen, setLegendModalIsOpen] = useState<boolean>(false);
  const [whatsUpIsOpen, setWhatsUpIsOpen] = useState<boolean>(false);
  const [propertyModalIsOpen, setPropertyModalIsOpen] = useState<boolean>(false);
  const [favoriteLandByParcCodeModalIsOpen, setFavoriteLandByParcCodeModalIsOpen] = useState<boolean>(false);
  const [editIsOpen, setEditIsOpen] = useState<boolean>(false);
  const [isOpenSignIn, setIsOpenSignIn] = useState<boolean>(false);
  const [logoPointsIsOpen, setLogoPointsIsOpen] = useState<boolean>(false);
  const [landOperationsIsOpen, setLandOperationsIsOpen] = useState<boolean>(false);
  const [infoModalIsOpen, setInfoModalIsOpen] = useState<boolean>(false);
  const [isTrackingMyLocationIsOpen, setTrackingMyLocationIsOpen] = useState<boolean>(false);
  const [trackingModalMode, setTrackingModalMode] = useState<string | null>("");
  const [trackingModalData, setTrackingModalData] = useState<any>(null);
  
  // Get initial values from localStorage or use defaults
  const getInitialFavoriteIcons = (): boolean => {
    try {
      const stored = localStorage.getItem('showFavoriteIcons');
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true; // Default value if localStorage fails
    }
  };

  // Per-property-type filter selection. Null means "not restored yet": the real
  // default depends on which property types the ads actually contain, so it is
  // filled in once they load.
  const getStoredFilterSelection = (): FilterSelection | null => {
    try {
      const stored = localStorage.getItem('offerFilterSelection');
      if (stored === null) {
        return null;
      }

      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null; // Default value if localStorage fails
    }
  };

  const getInitialWalletIcons = (): boolean => {
    try {
      const stored = localStorage.getItem('showWalletIcons');
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true; // Default value if localStorage fails
    }
  };

  const [showFavoriteIcons, setShowFavoriteIcons] = useState<boolean>(getInitialFavoriteIcons);
  const [mapAds, setMapAds] = useState<MapAd[]>([]);
  const [offerFilterSelection, setOfferFilterSelection] = useState<FilterSelection | null>(getStoredFilterSelection);
  const [showWalletIcons, setShowWalletIcons] = useState<boolean>(getInitialWalletIcons);
  const [measuringToolsDropdownOpen, setMeasuringToolsDropdownOpen] = useState<boolean>(false);
  const [filtersDropdownOpen, setFiltersDropdownOpen] = useState<boolean>(false);

  // Wrapper functions that save to localStorage
  const setShowFavoriteIconsWithStorage = (value: boolean) => {
    try {
      localStorage.setItem('showFavoriteIcons', JSON.stringify(value));
    } catch {
      // Silently fail if localStorage is not available
    }
    setShowFavoriteIcons(value);
  };

  const setOfferFilterSelectionWithStorage = (value: FilterSelection) => {
    try {
      localStorage.setItem('offerFilterSelection', JSON.stringify(value));
    } catch {
      // Silently fail if localStorage is not available
    }
    setOfferFilterSelection(value);
  };

  const setShowWalletIconsWithStorage = (value: boolean) => {
    try {
      localStorage.setItem('showWalletIcons', JSON.stringify(value));
    } catch {
      // Silently fail if localStorage is not available
    }
    setShowWalletIcons(value);
  };

  return {
    isLoading, setIsLoading,
    filterModalIsOpen, setFilterModalIsOpen,
    legendModalIsOpen, setLegendModalIsOpen,
    whatsUpIsOpen, setWhatsUpIsOpen,
    propertyModalIsOpen, setPropertyModalIsOpen,
    favoriteLandByParcCodeModalIsOpen, setFavoriteLandByParcCodeModalIsOpen,
    editIsOpen, setEditIsOpen,
    isOpenSignIn, setIsOpenSignIn,
    logoPointsIsOpen, setLogoPointsIsOpen,
    landOperationsIsOpen, setLandOperationsIsOpen,
    infoModalIsOpen, setInfoModalIsOpen,
    isTrackingMyLocationIsOpen, setTrackingMyLocationIsOpen,
    trackingModalMode, setTrackingModalMode,
    trackingModalData, setTrackingModalData,
    showFavoriteIcons, setShowFavoriteIcons: setShowFavoriteIconsWithStorage,
    mapAds, setMapAds,
    offerFilterSelection, setOfferFilterSelection: setOfferFilterSelectionWithStorage,
    showWalletIcons, setShowWalletIcons: setShowWalletIconsWithStorage,
    measuringToolsDropdownOpen, setMeasuringToolsDropdownOpen,
    filtersDropdownOpen, setFiltersDropdownOpen,
  };
};

// Error State
export const useErrorState = () => {
  const [errorTitle, setErrorTitle] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  return {
    errorTitle, setErrorTitle,
    errorMessage, setErrorMessage,
  };
};

// User Role State
export const useUserRoleState = () => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isUser, setIsUser] = useState<boolean>(false);
  const [isOperation, setIsOperation] = useState<boolean>(false);

  return {
    isAdmin, setIsAdmin,
    isUser, setIsUser,
    isOperation, setIsOperation,
  };
};

// Feature & Operation State
export const useFeatureState = () => {
  const [isCreateFavoriteLand, setIsCreateFavoriteLand] = useState<boolean>(false);
  const [isCreateBuildingsWallet, setIsCreateBuildingsWallet] = useState<boolean>(false);
  const [selectedFeature, setSelectedFeature] = useState<any>();
  const [currentSelectedFeatures, setCurrentSelectedFeatures] = useState<any>();
  const [property, setProperty] = useState<PointDataResult>();
  const [getLogosData, SetGetLogosData] = useState<any>();

  return {
    isCreateFavoriteLand, setIsCreateFavoriteLand,
    isCreateBuildingsWallet, setIsCreateBuildingsWallet,
    selectedFeature, setSelectedFeature,
    currentSelectedFeatures, setCurrentSelectedFeatures,
    property, setProperty,
    getLogosData, SetGetLogosData,
  };
};

// Logo & Operation Values State
export const useLogoState = () => {
  const [landOperationsValue, setLandOperationsValue] = useState<string>("");
  const [logoPointsTypeValue, setLogoPointsTypeValue] = useState<string>("");
  const [logoPointsActiveValue, setLogoPointsActiveValue] = useState<string>("");
  const [logoPointsZoomLevelValue, setLogoPointsZoomLevelValue] = useState<string>("");
  const [logoFile, setLogoFile] = useState<any>();
  const [objectId, setObjectId] = useState<any>("");
  const [logoId, setLogoId] = useState<any>("");
  const [mapObjectId, setMapObjectId] = useState<any>("");
  const [parcel_Cod, setParcel_Cod] = useState<any>("");
  const [parcelNumber, setParcelNumber] = useState<any>("");
  const [sUBDIVISIONPLAN_ID, setSUBDIVISIONPLAN_ID] = useState<any>("");

  return {
    landOperationsValue, setLandOperationsValue,
    logoPointsTypeValue, setLogoPointsTypeValue,
    logoPointsActiveValue, setLogoPointsActiveValue,
    logoPointsZoomLevelValue, setLogoPointsZoomLevelValue,
    logoFile, setLogoFile,
    objectId, setObjectId,
    logoId, setLogoId,
    mapObjectId, setMapObjectId,
    parcel_Cod, setParcel_Cod,
    parcelNumber, setParcelNumber,
    sUBDIVISIONPLAN_ID, setSUBDIVISIONPLAN_ID,
  };
};

// Location & Property State
export const useLocationState = () => {
  const [currentLongitude, setCurrentLongitude] = useState<number>(0);
  const [currentLatitude, setCurrentLatitude] = useState<number>(0);
  const [region_Name, setregion_Name] = useState<string>("");
  const [city_Name, setcity_Name] = useState<string>("");
  const [district_Name, setdistrict_Name] = useState<string>("");
  const [currentRegion, setCurrentRegion] = useState<string | null>(null);

  return {
    currentLongitude, setCurrentLongitude,
    currentLatitude, setCurrentLatitude,
    city_Name, setcity_Name,
    district_Name, setdistrict_Name,
    region_Name, setregion_Name,
    currentRegion, setCurrentRegion,
  };
};

// Property Data State
export const usePropertyDataState = () => {
  const [propertyAdvertisements, setPropertyAdvertisements] = useState<any>([]);
  const [propertyFavoriteLand, setPropertyFavoriteLand] = useState<any>([]);
  const [isHaveF_FavoriteLand, setIsHaveF_FavoriteLand] = useState<any[]>([]);
  const [currentEventClick, setCurrentEventClick] = useState();
  const [EstateBrokersById, setEstateBrokersById] = useState<any>();

  return {
    propertyAdvertisements, setPropertyAdvertisements,
    propertyFavoriteLand, setPropertyFavoriteLand,
    isHaveF_FavoriteLand, setIsHaveF_FavoriteLand,
    currentEventClick, setCurrentEventClick,
    EstateBrokersById, setEstateBrokersById,
  };
};

// Search & Modal State
export const useSearchState = () => {
  const [isModalOpen, setIsModalOpen] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState<any[]>([]);

  return {
    isModalOpen, setIsModalOpen,
    searchQuery, setSearchQuery,
    filteredData, setFilteredData,
  };
};

// Integration State
export const useIntegrationState = () => {
  const [adLicenseNumber, setadLicenseNumber] = useState<string>();
  const [itItemData, setItemData] = useState<any>(undefined);

  return {
    adLicenseNumber, setadLicenseNumber,
    itItemData, setItemData,
  };
};
